import React, { useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { loadStripe } from '@stripe/stripe-js'
import moment from 'moment'
import { Link } from 'react-router-dom'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import Loader from 'src/components/Loader'
import StepPackagePricing from 'src/views/pages/register/startNewGame/StepPackagePricing'
import {
  CLUB_TEAM_BANDS,
  LEAGUE_TEAM_BANDS,
  CLUB_SERVICE_PACKAGES,
  DURATION_OPTIONS,
  getClubBasePrice,
  getLeagueBasePrice,
  computePricing,
} from 'src/views/pages/register/startNewGame/gameSetupConfig'
import StepperBar from '../SetupProgress/StepperBar'

// Replaces the old table-based /pricing-plan page (PricingTableForGame.js and its
// PricingPlanForClub/PricingPlanForLeagues children). This page reuses the exact
// "Package & Pricing" step from the "Start New Game" wizard
// (src/views/pages/register/startNewGame/StepPackagePricing.js) so an admin sees the
// same package/team-band/duration picker and pricing logic here as at signup, prefilled
// with whatever they picked when the game was created.
//
// Match Contest (game_type 4) is left exactly as it was - an embedded Stripe pricing
// table - since its pricing model wasn't part of this redesign.

const FALLBACK_CATEGORY_LABEL = {
  1: 'Club Competition',
  2: 'League / Tournament Competition (Outdoor)',
  3: 'League / Tournament Competition (Indoor)',
  4: 'Match Contest',
}

const getBadgeColor = (status) => {
  switch (status) {
    case 1:
      return 'success'
    case 2:
      return 'danger'
    default:
      return 'warning'
  }
}

const GamePricingUpgrade = () => {
  const [loading, setLoading] = useState(true)
  const [gameInfo, setGameInfo] = useState(null)
  const [gameExtraInfo, setGameExtraInfo] = useState(null)
  const [activationInfo, setActivationInfo] = useState(null)
  const [teamCount, setTeamCount] = useState(0)
  const [gamePlanTransactions, setGamePlanTransactions] = useState([])

  const [servicePackage, setServicePackage] = useState('')
  const [teamBand, setTeamBand] = useState('')
  const [duration, setDuration] = useState('season')
  const [payingNow, setPayingNow] = useState(false)

  const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_KEY), [])

  const isClub = gameInfo?.game_type === 1
  const isMatchContest = gameInfo?.game_type === 4

  useEffect(() => {
    Promise.all([CommonService.getGameActivateInfo(), CommonService.getGamePricingData()])
      .then(([activateRes, pricingRes]) => {
        if (activateRes.status === 200) {
          setGameInfo(activateRes.game_info)
          setGameExtraInfo(activateRes.game_extra_info)
          setActivationInfo(activateRes.getGameActivationInfo)

          // The package/team-band/duration picked at signup (or last edited here) lives
          // as an unpaid (is_paid = 0) game_pricing_plans row - that's what prefills the
          // picker below. Once a game has an activated/paid plan instead, that takes
          // precedence so an upgrade starts from what's actually active.
          const pending = activateRes.pendingPricingPlan
          const activePlan = activateRes.gamePricingPlanDetails
          const currentPlan = activePlan || pending
          const gameType = activateRes.game_info?.game_type

          if (currentPlan?.service_package) {
            setServicePackage(currentPlan.service_package)
          } else {
            setServicePackage(gameType === 1 ? 'managed_services' : 'season_package')
          }

          // Games created before this wizard existed (or where no plan was recorded)
          // have no matching band from min/max_teams - fall back to the band that
          // matches the club's actual current team count, so the indicative price is
          // visible immediately instead of an empty, unselected picker.
          const bands = gameType === 1 ? CLUB_TEAM_BANDS : LEAGUE_TEAM_BANDS
          const planBand = bands.find((b) => {
            if (currentPlan?.min_teams == null) return false
            // eslint-disable-next-line eqeqeq
            return b.min == currentPlan.min_teams && b.max == currentPlan.max_teams
          })
          const count = pricingRes?.teamCount
          const matchedBand = bands.find(
            (b) => count >= b.min && (b.max === null || count <= b.max),
          )
          // The recorded plan can be stale if teams were added since it was picked
          // (at signup or on a previous visit here) - never default below the band the
          // game's actual current team count requires, only above it.
          const bandIndex = (band) => (band ? bands.findIndex((b) => b.value === band.value) : -1)
          const effectiveBand =
            bandIndex(matchedBand) > bandIndex(planBand) ? matchedBand : planBand
          setTeamBand(effectiveBand?.value || matchedBand?.value || '')
          setDuration(currentPlan?.competition_duration || 'season')
        }
        if (pricingRes.status === 200) {
          setTeamCount(pricingRes.teamCount)
        }
      })
      .catch(() => ToastComponent('Something went wrong loading pricing details.', 'error'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (isMatchContest) return
    CommonService.getGamePricingPlanRecords().then((res) => {
      if (res.status === 200) setGamePlanTransactions(res.data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMatchContest])

  useEffect(() => {
    // Stripe's embedded pricing-table widget (Match Contest only) needs its script tag present.
    if (!isMatchContest) return undefined
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/pricing-table.js'
    script.async = true
    document.body.appendChild(script)
    return () => document.body.removeChild(script)
  }, [isMatchContest])

  // This page doubles as first-time activation and later plan changes: before the
  // game has ever been paid/approved, the primary action is "Pay Now"; once it's
  // activated, the same button becomes "Upgrade" for changing the plan.
  const hasActivated = activationInfo?.is_paid === 1

  const applyDurationAdjustment = isClub
  const teamBands = isClub ? CLUB_TEAM_BANDS : LEAGUE_TEAM_BANDS
  const selectedBand = teamBands.find((b) => b.value === teamBand)

  const basePrice = isClub
    ? getClubBasePrice(servicePackage, teamBand)
    : getLeagueBasePrice(gameExtraInfo?.competition_category, teamBand)
  const pricing = computePricing(basePrice, duration, applyDurationAdjustment)

  // Sum of everything actually paid so far (gamePlanTransactions is already the
  // is_paid = 1 rows only) - credited against the new plan's price so an upgrade only
  // ever charges the difference, never the full price again.
  const totalPaid = gamePlanTransactions.reduce((sum, r) => sum + (Number(r.amount) || 0), 0)
  const amountDue = pricing.finalPrice === null ? null : Math.max(0, pricing.finalPrice - totalPaid)
  const isNoOpSelection = totalPaid > 0 && amountDue !== null && amountDue <= 0

  const servicePackageLabel = CLUB_SERVICE_PACKAGES.find((p) => p.value === servicePackage)?.label
  const durationLabel = DURATION_OPTIONS.find((d) => d.value === duration)?.label

  const categoryLabel =
    gameExtraInfo?.competition_category
      ?.split('_')
      .map((w) => w[0]?.toUpperCase() + w.slice(1))
      .join(' ') ||
    FALLBACK_CATEGORY_LABEL[gameInfo?.game_type] ||
    ''

  const isBelowTeamCount = selectedBand && selectedBand.max !== null && teamCount > selectedBand.max

  const handlePayNow = async () => {
    if (!teamBand || !duration) {
      ToastComponent('Please select a team size and duration.', 'error')
      return
    }
    if (isBelowTeamCount) {
      ToastComponent(
        `This plan only supports up to ${selectedBand.max} teams, but you currently have ${teamCount} active teams. Please choose a higher tier, or remove teams to downgrade.`,
        'error',
      )
      return
    }
    if (isNoOpSelection) {
      ToastComponent(
        'This plan is already covered by what you have paid. Please choose a higher-value option to upgrade.',
        'error',
      )
      return
    }
    setPayingNow(true)
    try {
      const stripe = await stripePromise
      const productName = [categoryLabel, servicePackageLabel, selectedBand?.label, durationLabel]
        .filter(Boolean)
        .join(' - ')
      const res = await CommonService.createCheckoutSession({
        amount: amountDue,
        product_name: productName,
        plan_type: 2,
        minTeams: selectedBand?.min,
        maxTeams: selectedBand?.max,
        service_package: servicePackage,
        duration,
      })
      if (res?.id) {
        const result = await stripe.redirectToCheckout({ sessionId: res.id })
        if (result.error) ToastComponent(result.error.message, 'error')
      } else {
        ToastComponent('Unable to create Stripe session.', 'error')
      }
    } catch (error) {
      const message = error?.response?.data?.error
      ToastComponent(message || 'Something went wrong starting checkout.', 'error')
    } finally {
      setPayingNow(false)
    }
  }

  if (loading) return <Loader />

  return (
    <>
      <StepperBar activeStepKey="pricing_plan" />
      <CCard className="mb-4">
        <CCardHeader>
          <strong>Pricing Plan{categoryLabel ? ` - ${categoryLabel}` : ''}</strong>
        </CCardHeader>
        <CCardBody>
          {isMatchContest ? (
            !activationInfo && (
              <stripe-pricing-table
                pricing-table-id="prctbl_1Qk2wAItsmZf8AkXjYi6mFME"
                publishable-key="pk_live_I2ZzJhqZwGTxEprRPJwLt4TE"
              ></stripe-pricing-table>
            )
          ) : (
            <>
              <p className="text-medium-emphasis">
                Active teams: <Link to="/teams">{teamCount}</Link>
              </p>
              <StepPackagePricing
                isClub={isClub}
                competitionCategory={gameExtraInfo?.competition_category}
                applyDurationAdjustment={applyDurationAdjustment}
                servicePackage={servicePackage}
                onServicePackageChange={setServicePackage}
                teamBand={teamBand}
                onTeamBandChange={setTeamBand}
                duration={duration}
                onDurationChange={setDuration}
                pricing={pricing}
                minPurchasePrice={totalPaid}
                currentTeamCount={teamCount}
              />

              <div className="mt-4">
                <CLoadingButton
                  color="primary"
                  loading={payingNow}
                  disabled={!teamBand || !duration || isNoOpSelection || isBelowTeamCount}
                  onClick={handlePayNow}
                >
                  {hasActivated ? 'Upgrade' : 'Pay Now'}
                </CLoadingButton>
              </div>
            </>
          )}
        </CCardBody>
      </CCard>

      {!isMatchContest && (
        <>
          <CCard className="mb-4">
            <CCardHeader>
              <strong>Transactions</strong>
            </CCardHeader>
            <CCardBody>
              <CTable>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell scope="col">SN</CTableHeaderCell>
                    <CTableHeaderCell scope="col">Team Tiers</CTableHeaderCell>
                    <CTableHeaderCell scope="col">Amount</CTableHeaderCell>
                    <CTableHeaderCell scope="col">Paid on</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {gamePlanTransactions.map((record, index) => (
                    <CTableRow key={record.id || index}>
                      <CTableHeaderCell scope="row">{index + 1}</CTableHeaderCell>
                      <CTableDataCell>{record.product_name}</CTableDataCell>
                      <CTableDataCell>${record.amount}</CTableDataCell>
                      <CTableDataCell>
                        {moment(record.created_at).format('D.MM.YYYY h:mm A')}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </>
      )}
    </>
  )
}

export default GamePricingUpgrade
